import React, { useMemo, useRef, useState } from "react";
import { Search, Sparkles, Clock } from "lucide-react";
import { PersistedIntelligenceSearch, formatRelativeTime } from "../Services/IntelligencePersistence";

interface GlobalSearchBarProps {
  initialKeyword?: string;
  onSearch: (term: string) => void;
  onSelectHistory?: (entry: PersistedIntelligenceSearch) => void;
  history?: PersistedIntelligenceSearch[];
  exploring?: boolean;
}

const QUICK_PHRASES = [
  "QA Manager with 12+ years experience",
  "Engineering Director in Pune",
  "Automation Architect with large team experience",
  "QA leader who handled 100+ engineers",
  "Engineering Manager from product companies",
  "Director with SaaS experience",
  "Senior QA leader open to opportunities",
];

export const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({
  initialKeyword = "",
  onSearch,
  onSelectHistory,
  history = [],
  exploring = false,
}) => {
  const [term, setTerm] = useState(initialKeyword);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const submit = (value?: string) => {
    const searchTerm = value !== undefined ? value : term;
    onSearch(searchTerm);
  };

  const matches = useMemo(() => {
    const q = term.trim().toLowerCase();
    const list = q
      ? history.filter((entry) => entry.term.toLowerCase().includes(q))
      : history;
    return list.slice(0, 6);
  }, [history, term]);

  const showDropdown = dropdownOpen && matches.length > 0;

  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4 md:p-5" ref={wrapperRef}>
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={16} className="text-indigo-500" />
        <label className="text-sm font-bold text-slate-700">
          Global Candidate Search
        </label>
      </div>

      <div className="flex flex-col md:flex-row gap-2.5">
        <div className="flex-1 relative">
          <Search
            size={18}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
          <input
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
              if (e.key === "Escape") setDropdownOpen(false);
            }}
            onFocus={() => setDropdownOpen(true)}
            onBlur={() => setDropdownOpen(false)}
            placeholder="Search candidates by role, skill, leadership experience, industry, company, location, impact or any professional signal…"
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 text-sm text-slate-700 placeholder:text-slate-400 transition-all"
          />

          {showDropdown && (
            <div
              className="absolute left-0 top-full mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-xl z-20 overflow-hidden"
              onMouseDown={(e) => e.preventDefault()}
            >
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center gap-1.5">
                <Clock size={11} className="text-slate-400" />
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  Recent searches
                </span>
              </div>
              <ul className="max-h-64 overflow-y-auto py-1">
                {matches.map((entry) => (
                  <li key={entry.id}>
                    <button
                      type="button"
                      onClick={() => {
                        onSelectHistory?.(entry);
                        setDropdownOpen(false);
                        setTerm(entry.term);
                      }}
                      className="w-full text-left px-3 py-2 flex items-center justify-between gap-2 hover:bg-indigo-50 transition-colors"
                    >
                      <span className="text-xs font-semibold text-slate-700 truncate">
                        {entry.term || <span className="text-slate-400">Filtered search</span>}
                      </span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        {entry.filterCount > 0 && (
                          <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 text-[9px] font-black">
                            {entry.filterCount}
                          </span>
                        )}
                        <span className="text-[9px] text-slate-400 font-medium">
                          {formatRelativeTime(entry.createdAt)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => submit()}
          disabled={exploring}
          className="px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-fuchsia-600 text-white text-sm font-bold hover:opacity-90 disabled:opacity-60 transition-all shadow-sm flex items-center justify-center gap-2"
        >
          <Search size={16} />
          {exploring ? "Searching…" : "Search"}
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5 items-center">
        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mr-1">
          Try:
        </span>
        {QUICK_PHRASES.map((phrase) => (
          <button
            key={phrase}
            type="button"
            onClick={() => {
              setTerm(phrase);
              submit(phrase);
            }}
            className="px-2.5 py-1 rounded-full bg-slate-50 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 border border-slate-200 text-[11px] font-semibold text-slate-600 transition-colors"
          >
            {phrase}
          </button>
        ))}
      </div>
    </div>
  );
};

export default GlobalSearchBar;